import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled, { keyframes } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { FaYoutube, FaFacebookF, FaInstagram, FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';
import { useData } from '../context/DataContext';
import { useLanguage } from '../context/LanguageContext';

// Keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.05); }
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

// Styled Components with Theme Support
const Main = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(135deg, #ffffff 0%, #f8f5ff 50%, #f0f7ff 100%)'
    : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'};
  color: ${props => props.themeMode === 'light' ? '#2d2d44' : '#fff'};
  overflow-x: hidden;
  transition: all 0.4s ease;
`;

const Hero = styled.section`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    min-height: 90vh;
    padding: 2rem 1rem;
  }
`;

const HeroCarousel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  
  .swiper {
    width: 100%;
    height: 100%;
  }
  
  .swiper-slide {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .swiper-pagination {
    bottom: 30px !important;
  }
  
  .swiper-pagination-bullet {
    width: 12px;
    height: 12px;
    background: rgba(255, 255, 255, 0.5);
    opacity: 1;
    transition: all 0.3s ease;
  }
  
  .swiper-pagination-bullet-active {
    background: #7c4dff;
    width: 30px;
    border-radius: 6px;
  }
`;

const HeroSlide = styled.div`
  width: 100%;
  height: 100%;
  background: ${props => props.image 
    ? `url(${props.image}) center/cover no-repeat` 
    : props.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.themeMode === 'light'
      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(248, 245, 255, 0.8) 50%, rgba(240, 247, 255, 0.7) 100%)'
      : 'linear-gradient(135deg, rgba(26, 26, 46, 0.7) 0%, rgba(22, 33, 62, 0.8) 50%, rgba(15, 52, 96, 0.7) 100%)'};
  }
`;

const FloatingShape = styled.div`
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(124, 77, 255, 0.3) 0%, rgba(83, 109, 254, 0.2) 100%);
  animation: ${float} ${props => props.duration || '6s'} ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  z-index: 1;
  pointer-events: none;
  
  ${props => props.size === 'large' && `
    width: 300px;
    height: 300px;
    top: -50px;
    right: -50px;
  `}
  
  ${props => props.size === 'medium' && `
    width: 200px;
    height: 200px;
    bottom: 10%;
    left: -30px;
  `}
  
  ${props => props.size === 'small' && `
    width: 100px;
    height: 100px;
    top: 40%;
    right: 5%;
  `}
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 2rem;
  max-width: 900px;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2rem, 7vw, 4.5rem);
  font-weight: 800;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(90deg, #7c4dff 0%, #536dfe 50%, #7c4dff 100%)'
    : 'linear-gradient(90deg, #b39ddb 0%, #90caf9 50%, #ce93d8 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(0.95rem, 2.5vw, 1.4rem);
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#b39ddb'};
  max-width: 700px;
  line-height: 1.8;
  margin: 0 auto 2rem;
  
  @media (max-width: 480px) {
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;

const HeroButton = styled(motion.a)`
  display: inline-block;
  padding: 1rem 3rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #7c4dff 0%, #536dfe 100%);
  border-radius: 50px;
  text-decoration: none;
  box-shadow: 0 8px 30px rgba(124, 77, 255, 0.4);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(124, 77, 255, 0.6);
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 2rem;
    font-size: 1rem;
  }
`;

const CarouselSection = styled.section`
  width: 100%;
  padding: 5rem 2rem;
  background: ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.03)'
    : 'rgba(255, 255, 255, 0.02)'};
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const CarouselContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  
  .swiper-slide img {
    width: 100%;
    height: 500px;
    object-fit: cover;
    border-radius: 20px;
    
    @media (max-width: 768px) {
      height: 350px;
      border-radius: 15px;
    }
    
    @media (max-width: 480px) {
      height: 250px;
      border-radius: 12px;
    }
  }
  
  .swiper-pagination-bullet {
    background: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  }
  
  .swiper-pagination-bullet-active {
    background: #7c4dff;
  }
  
  .swiper-button-next, .swiper-button-prev {
    color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
    
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const CarouselPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  min-height: 500px;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(135deg, #f5f0ff 0%, #e8f4ff 100%)'
    : 'linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  font-size: 1.2rem;
  flex-direction: column;
  text-align: center;
  padding: 2rem;
  
  @media (max-width: 768px) {
    min-height: 350px;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    min-height: 250px;
    font-size: 0.9rem;
  }
`;

const Section = styled(motion.section)`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 2.5rem 1rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(1.8rem, 4vw, 3rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(90deg, #7c4dff 0%, #536dfe 100%)'
    : 'linear-gradient(90deg, #b39ddb 0%, #90caf9 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const AboutCard = styled(motion.div)`
  background: ${props => props.themeMode === 'light'
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2.5rem;
  border: 1px solid ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.15)'
    : 'rgba(179, 157, 219, 0.2)'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.themeMode === 'light'
    ? '0 10px 40px rgba(124, 77, 255, 0.1)'
    : 'none'};
  
  &:hover {
    transform: translateY(-10px);
    border-color: #7c4dff;
    box-shadow: 0 20px 40px rgba(124, 77, 255, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const CardText = styled.p`
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#a0a0c0'};
  line-height: 1.8;
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.7;
  }
`;

// ============ LEADERS SECTION - ORGANIZATIONAL CHART ============
const LeadersSection = styled.section`
  width: 100%;
  padding: 4rem 1rem;
  background: linear-gradient(180deg, #5b4b8a 0%, #4a3d7a 50%, #3d2d6a 100%);
  overflow-x: visible;
  
  @media (max-width: 768px) {
    padding: 2rem 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 0.25rem;
  }
`;

const OrgChartContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 0 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 0.5rem;
  }
`;

const OrgChart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

// Top Leaders Row (Side by side with connecting line)
const TopLeadersRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 80px;
  position: relative;
  flex-wrap: wrap;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 40px;
  }
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const TopConnectorLine = styled.div`
  position: absolute;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 3px;
  background: #ffd700;
  
  @media (max-width: 768px) {
    width: 120px;
    top: 50px;
  }
  
  @media (max-width: 600px) {
    display: none;
  }
`;

const LeaderCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 200px;
  
  @media (max-width: 480px) {
    max-width: 160px;
  }
`;

const LeaderPhoto = styled.div`
  width: ${props => props.size || '100px'};
  height: ${props => props.size || '100px'};
  border-radius: 50%;
  background: ${props => props.image 
    ? `url(${props.image}) center/cover no-repeat` 
    : 'linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)'};
  border: 4px solid ${props => props.borderColor || '#ffd700'};
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1rem;
  font-weight: bold;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: ${props => props.mobileSize || '85px'};
    height: ${props => props.mobileSize || '85px'};
    border-width: 3px;
  }
  
  @media (max-width: 480px) {
    width: ${props => props.mobileSize || '70px'};
    height: ${props => props.mobileSize || '70px'};
  }
`;

const LeaderInfoBox = styled.div`
  background: linear-gradient(135deg, #4a90d9 0%, #357abd 100%);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  margin-top: 0.5rem;
  text-align: center;
  min-width: 150px;
  max-width: 200px;
  
  @media (max-width: 768px) {
    min-width: 130px;
    max-width: 180px;
    padding: 0.4rem 0.8rem;
  }
  
  @media (max-width: 480px) {
    min-width: 110px;
    max-width: 150px;
    padding: 0.3rem 0.6rem;
  }
`;

const LeaderName = styled.h4`
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.65rem;
  }
`;

const LeaderRole = styled.p`
  color: ${props => props.color || '#ffd700'};
  font-size: 0.65rem;
  margin: 0.2rem 0 0;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 0.6rem;
  }
`;

// Vertical Connector Line
const VerticalLine = styled.div`
  width: 3px;
  height: ${props => props.height || '40px'};
  background: ${props => props.color || '#ffd700'};
  margin: 0 auto;
`;

// Horizontal Connector Line between columns
const HorizontalConnector = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 3px;
  background: #ffd700;
  
  @media (max-width: 900px) {
    display: none;
  }
`;

// Single Centered Leader
const SingleLeaderRow = styled.div`
  display: flex;
  justify-content: center;
`;

// Two Column Layout for departments
const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  width: 100%;
  margin-top: 30px;
  position: relative;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
    padding: 0;
  }
`;

// Three Column Layout
const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1.5rem;
  width: 100%;
  margin-top: 30px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 900px) {
    padding: 0 0.5rem;
    align-items: center;
    
    &::before {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0 0.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 0;
  }
`;

// Ministry Row (Left column - no connecting lines, just label + members)
const MinistryGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0.6rem 0;
  width: 100%;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
`;

const MinistryLabel = styled.div`
  background: ${props => props.bgColor || '#ffd700'};
  color: ${props => props.textColor || '#1a1a2e'};
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-weight: 800;
  font-size: 0.65rem;
  text-transform: uppercase;
  min-width: 120px;
  text-align: center;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    min-width: auto;
    font-size: 0.6rem;
  }
`;

const MinistryMembers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex: 1;
`;

const MemberCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.12);
  padding: 0.4rem 0.6rem;
  border-radius: 8px;
`;

const MemberPhoto = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.image 
    ? `url(${props.image}) center/cover no-repeat` 
    : 'linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)'};
  border: 2px solid ${props => props.borderColor || '#ffd700'};
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }
`;

const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MemberName = styled.p`
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  margin: 0;
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const MemberRole = styled.p`
  color: ${props => props.color || '#ffd700'};
  font-size: 0.6rem;
  margin: 0;
  font-weight: 500;
`;

// Department Box
const DepartmentBox = styled.div`
  background: #ffd700;
  color: #1a1a2e;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 800;
  font-size: 0.8rem;
  text-transform: uppercase;
  text-align: center;
  margin: 0.75rem 0;
  width: 100%;
  max-width: 300px;
  
  @media (max-width: 480px) {
    font-size: 0.65rem;
    padding: 0.4rem 0.8rem;
    max-width: 100%;
  }
`;

// Staff Card (for departments)
const StaffCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: rgba(255, 255, 255, 0.12);
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  margin: 0.25rem 0;
  width: 100%;
  max-width: 300px;
  
  @media (max-width: 480px) {\n    max-width: 100%;
    padding: 0.4rem 0.5rem;
    gap: 0.4rem;
  }
`;

const StaffPhoto = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: ${props => props.image 
    ? `url(${props.image}) center/cover no-repeat` 
    : 'linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)'};
  border: 2px solid ${props => props.borderColor || '#ffd700'};
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
  }
`;

const StaffInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StaffName = styled.p`
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  margin: 0;
  word-wrap: break-word;
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const StaffRole = styled.p`
  color: ${props => props.color || '#ffd700'};
  font-size: 0.6rem;
  margin: 0;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 0.55rem;
  }
`;

// Facilities Section - Clean arrangement like reference image
const FacilitiesSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  width: 100%;
  
  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-top: 1rem;
  }
`;

const FacilitiesCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, #4a90d9 0%, #357abd 100%);
  padding: 0.8rem 1.5rem;
  border-radius: 40px;
  width: 100%;
  max-width: 350px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    max-width: 320px;
    padding: 0.7rem 1.2rem;
    gap: 0.8rem;
    border-radius: 35px;
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
    padding: 0.6rem 1rem;
    gap: 0.6rem;
    border-radius: 30px;
  }
`;

const FacilitiesPhoto = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.image 
    ? `url(${props.image}) center/cover no-repeat` 
    : 'linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)'};
  border: 3px solid #00bcd4;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    width: 55px;
    height: 55px;
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    border-width: 2px;
  }
`;

const FacilitiesInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FacilitiesName = styled.h4`
  color: #fff;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const FacilitiesRole = styled.p`
  color: #b3e5fc;
  font-size: 0.85rem;
  margin: 0.25rem 0 0;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const MapSection = styled.section`
  width: 100%;
  padding: 5rem 2rem;
  background: ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.03)'
    : 'rgba(0, 0, 0, 0.2)'};
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const MapContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: ${props => props.themeMode === 'light'
    ? '0 20px 60px rgba(124, 77, 255, 0.15)'
    : '0 20px 60px rgba(0, 0, 0, 0.3)'};
  
  iframe {
    @media (max-width: 768px) {
      height: 350px !important;
    }
    
    @media (max-width: 480px) {
      height: 280px !important;
    }
  }
  
  @media (max-width: 768px) {
    border-radius: 15px;
  }
`;

const ContactSection = styled.section`
  width: 100%;
  padding: 5rem 2rem;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(135deg, rgba(124, 77, 255, 0.05) 0%, rgba(83, 109, 254, 0.05) 100%)'
    : 'linear-gradient(135deg, rgba(106, 27, 154, 0.2) 0%, rgba(57, 73, 171, 0.2) 100%)'};
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ContactCard = styled(motion.div)`
  background: ${props => props.themeMode === 'light'
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  border: 1px solid ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.15)'
    : 'rgba(179, 157, 219, 0.2)'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.themeMode === 'light'
    ? '0 10px 30px rgba(124, 77, 255, 0.1)'
    : 'none'};
  
  &:hover {
    border-color: #7c4dff;
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
`;

const ContactIcon = styled.div`
  font-size: 2.5rem;
  color: #7c4dff;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const ContactText = styled.p`
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#b39ddb'};
  font-size: 1.1rem;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const SocialSection = styled.section`
  width: 100%;
  padding: 5rem 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    gap: 1.5rem;
  }
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.1)'
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  font-size: 1.8rem;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    background: linear-gradient(135deg, #7c4dff 0%, #536dfe 100%);
    color: #fff;
    transform: scale(1.1);
    border-color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }
`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

// Scroll animation hook
const useScrollAnimation = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  
  return { ref, controls };
};

// Animated Section Component
const AnimatedSection = ({ children, ...props }) => {
  const { ref, controls } = useScrollAnimation();
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeInUp}
      {...props}
    >
      {children}
    </motion.div>
  );
};

function Home() {
  const { theme } = useTheme();
  const { data } = useData();
  const { t } = useLanguage();
  const themeMode = theme.mode;
  
  // Get hero slides from data context
  const heroSlides = data.heroSlides || [
    { id: 1, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', image: '' },
    { id: 2, gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', image: '' },
    { id: 3, gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', image: '' },
    { id: 4, gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', image: '' },
    { id: 5, gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', image: '' },
  ];

  // Use carouselImages from data context
  const carouselImages = data.carouselImages || [];

  return (
    <Main themeMode={themeMode}>
      {/* Hero Section with Background Carousel */}
      <Hero>
        {/* Floating decorative shapes */}
        <FloatingShape size="large" duration="8s" delay="0s" />
        <FloatingShape size="medium" duration="6s" delay="1s" />
        <FloatingShape size="small" duration="7s" delay="0.5s" />
        
        <HeroCarousel>
          <Swiper
            modules={[Autoplay, EffectFade, Pagination]}
            effect="fade"
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            loop={true}
            speed={1500}
          >
            {heroSlides.map((slide) => (
              <SwiperSlide key={slide.id}>
                <HeroSlide 
                  gradient={slide.gradient} 
                  image={slide.image}
                  themeMode={themeMode}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </HeroCarousel>
        
        <HeroContent>
          <HeroTitle
            themeMode={themeMode}
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            {data.churchInfo?.name || 'House Of Worship Davao'}
          </HeroTitle>
          <HeroSubtitle
            themeMode={themeMode}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {data.churchInfo?.tagline || 'Where would we be if not for "God\'s Grace"'}<br />
            {data.churchInfo?.description || 'This is our living testimony! Our desire is to help you discover Jesus, His promises and Glorious plan in your life.'}
          </HeroSubtitle>
          <HeroButton
            href="#about"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t.home.learnMore}
          </HeroButton>
        </HeroContent>
      </Hero>

      {/* Photo Carousel Section */}
      <CarouselSection themeMode={themeMode}>
        <AnimatedSection>
          <SectionTitle themeMode={themeMode}>Our Community</SectionTitle>
          <CarouselContainer themeMode={themeMode}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              loop={true}
              breakpoints={{
                640: { slidesPerView: 1 },
                768: { slidesPerView: 1 },
                1024: { slidesPerView: 1 },
              }}
            >
              {carouselImages.filter(img => img.image).length > 0 ? (
                carouselImages.filter(img => img.image).map((img) => (
                  <SwiperSlide key={img.id}>
                    <div style={{ 
                      width: '100%', 
                      height: '400px', 
                      borderRadius: '20px',
                      overflow: 'hidden',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                    }}>
                      <img 
                        src={img.image} 
                        alt={img.title || 'Church community'}
                        loading="eager"
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          imageRendering: '-webkit-optimize-contrast',
                          backfaceVisibility: 'hidden',
                          transform: 'translateZ(0)'
                        }}
                      />
                    </div>
                  </SwiperSlide>
                ))
              ) : (
                <SwiperSlide>
                  <CarouselPlaceholder themeMode={themeMode}>
                    📷 Church Community Photos
                  </CarouselPlaceholder>
                </SwiperSlide>
              )}
            </Swiper>
          </CarouselContainer>
        </AnimatedSection>
      </CarouselSection>

      {/* About Section */}
      <Section id="about">
        <AnimatedSection>
          <SectionTitle themeMode={themeMode}>{t.home.aboutUs}</SectionTitle>
          <AboutGrid>
            <AboutCard themeMode={themeMode} whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
              <CardTitle themeMode={themeMode}>🙏 Our Mission</CardTitle>
              <CardText themeMode={themeMode}>
                To spread the love of Jesus Christ and build a community of believers 
                who worship, grow, and serve together in faith and unity.
              </CardText>
            </AboutCard>
            <AboutCard themeMode={themeMode} whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
              <CardTitle themeMode={themeMode}>✨ Our Vision</CardTitle>
              <CardText themeMode={themeMode}>
                To be a beacon of hope and transformation in Davao, reaching hearts 
                and changing lives through the power of God's grace and love.
              </CardText>
            </AboutCard>
            <AboutCard themeMode={themeMode} whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
              <CardTitle themeMode={themeMode}>❤️ Our Values</CardTitle>
              <CardText themeMode={themeMode}>
                Faith, Love, Community, Service, and Excellence. We believe in living 
                out these values daily as we journey together in Christ.
              </CardText>
            </AboutCard>
          </AboutGrid>
        </AnimatedSection>
      </Section>

      {/* Church Leaders Section - Organizational Chart */}
      <LeadersSection id="leaders">
        <AnimatedSection>
          <SectionTitle themeMode={themeMode} style={{ color: '#fff', marginBottom: '3rem' }}>
            🏛️ Organizational Structure
          </SectionTitle>
          <OrgChartContainer>
            <OrgChart>
              {/* TOP LEVEL - Two Senior Pastors Side by Side */}
              <TopLeadersRow>
                <TopConnectorLine />
                
                {/* Left - Senior Pastor */}
                {(() => {
                  const seniorPastor = (data.aboutContent?.team || []).find(l => l.level === 1 && l.order === 1);
                  return seniorPastor && (
                    <LeaderCard>
                      <LeaderPhoto 
                        size="110px" 
                        mobileSize="85px"
                        image={seniorPastor.image} 
                        borderColor="#ffd700"
                      >
                        {!seniorPastor.image && seniorPastor.initials}
                      </LeaderPhoto>
                      <LeaderInfoBox>
                        <LeaderName>{seniorPastor.name}</LeaderName>
                        <LeaderRole>{seniorPastor.role}</LeaderRole>
                      </LeaderInfoBox>
                    </LeaderCard>
                  );
                })()}
                
                {/* Right - Overseer Pastor */}
                {(() => {
                  const overseerPastor = (data.aboutContent?.team || []).find(l => l.level === 1 && l.order === 2);
                  return overseerPastor && (
                    <LeaderCard>
                      <LeaderPhoto 
                        size="110px" 
                        mobileSize="85px"
                        image={overseerPastor.image} 
                        borderColor="#e91e63"
                      >
                        {!overseerPastor.image && overseerPastor.initials}
                      </LeaderPhoto>
                      <LeaderInfoBox>
                        <LeaderName>{overseerPastor.name}</LeaderName>
                        <LeaderRole color="#ff9800">{overseerPastor.role}</LeaderRole>
                      </LeaderInfoBox>
                    </LeaderCard>
                  );
                })()}
              </TopLeadersRow>

              {/* Vertical Line */}
              <VerticalLine height="40px" />

              {/* LEVEL 2 - Host Pastor */}
              <SingleLeaderRow>
                {(() => {
                  const hostPastor = (data.aboutContent?.team || []).find(l => l.level === 2);
                  return hostPastor && (
                    <LeaderCard>
                      <LeaderPhoto 
                        size="95px" 
                        mobileSize="75px"
                        image={hostPastor.image} 
                        borderColor="#7c4dff"
                      >
                        {!hostPastor.image && hostPastor.initials}
                      </LeaderPhoto>
                      <LeaderInfoBox>
                        <LeaderName>{hostPastor.name}</LeaderName>
                        <LeaderRole>{hostPastor.role}</LeaderRole>
                      </LeaderInfoBox>
                    </LeaderCard>
                  );
                })()}
              </SingleLeaderRow>

              {/* Vertical Line */}
              <VerticalLine height="35px" />

              {/* LEVEL 3 - Assistant Host Pastor */}
              <SingleLeaderRow>
                {(() => {
                  const assistantPastor = (data.aboutContent?.team || []).find(l => l.level === 3);
                  return assistantPastor && (
                    <LeaderCard>
                      <LeaderPhoto 
                        size="85px" 
                        mobileSize="70px"
                        image={assistantPastor.image} 
                        borderColor="#00bcd4"
                      >
                        {!assistantPastor.image && assistantPastor.initials}
                      </LeaderPhoto>
                      <LeaderInfoBox>
                        <LeaderName>{assistantPastor.name}</LeaderName>
                        <LeaderRole color="#b2ff59">{assistantPastor.role}</LeaderRole>
                      </LeaderInfoBox>
                    </LeaderCard>
                  );
                })()}
              </SingleLeaderRow>

              {/* Vertical Line to Two Columns */}
              <VerticalLine height="35px" />

              {/* Two Column Grid */}
              <TwoColumnGrid>
                <HorizontalConnector />
                
                {/* LEFT COLUMN - All Ministries */}
                <Column>
                  {/* Men's Ministry */}
                  <DepartmentBox style={{ background: '#ffd700', color: '#1a1a2e' }}>Men's Ministry</DepartmentBox>
                  {(data.aboutContent?.team || [])
                    .filter(l => l.ministry === 'men')
                    .sort((a, b) => a.order - b.order)
                    .map(leader => (
                      <StaffCard key={leader.id}>
                        <StaffPhoto image={leader.image} borderColor="#ffd700" />
                        <StaffInfo>
                          <StaffName>{leader.name}</StaffName>
                          <StaffRole>{leader.role}</StaffRole>
                        </StaffInfo>
                      </StaffCard>
                    ))}

                  {/* Women's Ministry */}
                  <DepartmentBox style={{ background: '#e91e63', color: '#fff' }}>Women's Ministry</DepartmentBox>
                  {(data.aboutContent?.team || [])
                    .filter(l => l.ministry === 'women')
                    .sort((a, b) => a.order - b.order)
                    .map(leader => (
                      <StaffCard key={leader.id}>
                        <StaffPhoto image={leader.image} borderColor="#e91e63" />
                        <StaffInfo>
                          <StaffName>{leader.name}</StaffName>
                          <StaffRole color="#e91e63">{leader.role}</StaffRole>
                        </StaffInfo>
                      </StaffCard>
                    ))}

                  {/* Children's Ministry */}
                  <DepartmentBox style={{ background: '#4caf50', color: '#fff' }}>Children's Ministry</DepartmentBox>
                  {(data.aboutContent?.team || [])
                    .filter(l => l.ministry === 'children')
                    .sort((a, b) => a.order - b.order)
                    .map(leader => (
                      <StaffCard key={leader.id}>
                        <StaffPhoto image={leader.image} borderColor="#4caf50" />
                        <StaffInfo>
                          <StaffName>{leader.name}</StaffName>
                          <StaffRole color="#4caf50">{leader.role}</StaffRole>
                        </StaffInfo>
                      </StaffCard>
                    ))}

                  {/* Youth Ministry */}
                  <DepartmentBox style={{ background: '#ff9800', color: '#1a1a2e' }}>Youth Ministry</DepartmentBox>
                  {(data.aboutContent?.team || [])
                    .filter(l => l.ministry === 'youth')
                    .sort((a, b) => a.order - b.order)
                    .map(leader => (
                      <StaffCard key={leader.id}>
                        <StaffPhoto image={leader.image} borderColor="#ff9800" />
                        <StaffInfo>
                          <StaffName>{leader.name}</StaffName>
                          <StaffRole color="#ff9800">{leader.role}</StaffRole>
                        </StaffInfo>
                      </StaffCard>
                    ))}

                  {/* Music Ministry */}
                  <DepartmentBox style={{ background: '#9c27b0', color: '#fff' }}>Music Ministry</DepartmentBox>
                  {(data.aboutContent?.team || [])
                    .filter(l => l.ministry === 'music')
                    .sort((a, b) => a.order - b.order)
                    .map(leader => (
                      <StaffCard key={leader.id}>
                        <StaffPhoto image={leader.image} borderColor="#9c27b0" />
                        <StaffInfo>
                          <StaffName>{leader.name}</StaffName>
                          <StaffRole color="#ce93d8">{leader.role}</StaffRole>
                        </StaffInfo>
                      </StaffCard>
                    ))}

                  {/* Evangelism Ministry */}
                  <DepartmentBox style={{ background: '#f44336', color: '#fff' }}>Evangelism Ministry</DepartmentBox>
                  {(data.aboutContent?.team || [])
                    .filter(l => l.ministry === 'evangelism')
                    .sort((a, b) => a.order - b.order)
                    .map(leader => (
                      <StaffCard key={leader.id}>
                        <StaffPhoto image={leader.image} borderColor="#f44336" />
                        <StaffInfo>
                          <StaffName>{leader.name}</StaffName>
                          <StaffRole color="#ff8a80">{leader.role}</StaffRole>
                        </StaffInfo>
                      </StaffCard>
                    ))}
                </Column>

                {/* RIGHT COLUMN - Admin, Accounting, Multimedia, Facilities */}
                <Column>
                  {/* Church Admin */}
                  {(() => {
                    const admin = (data.aboutContent?.team || []).find(l => l.department === 'admin');
                    return admin && (
                      <StaffCard>
                        <StaffPhoto image={admin.image} borderColor="#7c4dff" />
                        <StaffInfo>
                          <StaffName>{admin.name}</StaffName>
                          <StaffRole color="#7c4dff">{admin.role}</StaffRole>
                        </StaffInfo>
                      </StaffCard>
                    );
                  })()}

                  {/* Accounting and Finance */}
                  <DepartmentBox>Accounting & Finance</DepartmentBox>
                  {(data.aboutContent?.team || [])
                    .filter(l => l.department === 'accounting')
                    .sort((a, b) => a.order - b.order)
                    .map(leader => (
                      <StaffCard key={leader.id}>
                        <StaffPhoto image={leader.image} borderColor="#ffd700" />
                        <StaffInfo>
                          <StaffName>{leader.name}</StaffName>
                          <StaffRole>{leader.role}</StaffRole>
                        </StaffInfo>
                      </StaffCard>
                    ))}

                  {/* Multimedia */}
                  <DepartmentBox>Multimedia</DepartmentBox>
                  {(data.aboutContent?.team || [])
                    .filter(l => l.department === 'multimedia')
                    .sort((a, b) => a.order - b.order)
                    .map(leader => (
                      <StaffCard key={leader.id}>
                        <StaffPhoto image={leader.image} borderColor="#00bcd4" />
                        <StaffInfo>
                          <StaffName>{leader.name}</StaffName>
                          <StaffRole color="#00bcd4">{leader.role}</StaffRole>
                        </StaffInfo>
                      </StaffCard>
                    ))}

                  {/* Facilities Ministry - Same style as other ministries */}
                  <DepartmentBox>Facilities Ministry</DepartmentBox>
                  {(data.aboutContent?.team || [])
                    .filter(l => l.department === 'facilities')
                    .sort((a, b) => a.level - b.level)
                    .map(leader => (
                      <StaffCard key={leader.id}>
                        <StaffPhoto image={leader.image} borderColor="#ffd700" />
                        <StaffInfo>
                          <StaffName>{leader.name}</StaffName>
                          <StaffRole>{leader.role}</StaffRole>
                        </StaffInfo>
                      </StaffCard>
                    ))}
                </Column>
              </TwoColumnGrid>
            </OrgChart>
          </OrgChartContainer>
        </AnimatedSection>
      </LeadersSection>

      {/* Google Map Section */}
      <MapSection themeMode={themeMode}>
        <AnimatedSection>
          <SectionTitle themeMode={themeMode}>📍 Find Us</SectionTitle>
          <MapContainer themeMode={themeMode}>
            <iframe
              title="Church Location"
              src={data.churchInfo?.mapEmbed || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d989.8909901885514!2d125.55401911959839!3d7.060409100000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x32f90d0046b2a34d%3A0xd513d93b104d9c75!2sHouse%20of%20Worship%20(HOW)!5e0!3m2!1sen!2sph!4v1735100000000!5m2!1sen!2sph"}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </MapContainer>
        </AnimatedSection>
      </MapSection>

      {/* Contact Section */}
      <ContactSection themeMode={themeMode} id="contact">
        <AnimatedSection>
          <SectionTitle themeMode={themeMode}>Contact Us</SectionTitle>
          <ContactGrid>
            <ContactCard themeMode={themeMode} whileHover={{ scale: 1.05 }}>
              <ContactIcon><FaMapMarkerAlt /></ContactIcon>
              <CardTitle themeMode={themeMode}>Address</CardTitle>
              <ContactText themeMode={themeMode}>
                {data.churchInfo?.address || 'Davao City, Philippines'}
              </ContactText>
            </ContactCard>
            <ContactCard themeMode={themeMode} whileHover={{ scale: 1.05 }}>
              <ContactIcon><FaPhone /></ContactIcon>
              <CardTitle themeMode={themeMode}>Phone</CardTitle>
              <ContactText themeMode={themeMode}>
                {data.churchInfo?.phone || '+63 XXX XXX XXXX'}
              </ContactText>
            </ContactCard>
            <ContactCard themeMode={themeMode} whileHover={{ scale: 1.05 }}>
              <ContactIcon><FaEnvelope /></ContactIcon>
              <CardTitle themeMode={themeMode}>Email</CardTitle>
              <ContactText themeMode={themeMode}>
                {data.churchInfo?.email || 'info@houseofworshipdavao.com'}
              </ContactText>
            </ContactCard>
          </ContactGrid>
        </AnimatedSection>
      </ContactSection>

      {/* Social Media Section */}
      <SocialSection>
        <AnimatedSection>
          <SectionTitle themeMode={themeMode}>Follow Us</SectionTitle>
          <SocialLinks>
            <SocialLink
              themeMode={themeMode}
              href={data.socialLinks?.youtube || "https://youtube.com"}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, rotate: 5 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaYoutube />
            </SocialLink>
            <SocialLink
              themeMode={themeMode}
              href={data.socialLinks?.facebook || "https://facebook.com"}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, rotate: -5 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaFacebookF />
            </SocialLink>
            <SocialLink
              themeMode={themeMode}
              href={data.socialLinks?.instagram || "https://instagram.com"}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, rotate: 5 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaInstagram />
            </SocialLink>
          </SocialLinks>
        </AnimatedSection>
      </SocialSection>
    </Main>
  );
}

export default Home;
